import { observer } from 'mobx-react-lite';

import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect } from 'react';
import { StockIndexResponse, StockResponse } from '../../../response/responseSchema';
import modalStore from '@client/store/modalStore';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import ChangeBarGraph, { ChangeBarGraphItem } from './ChangeBarGraph';
import TargetReturnButton from './TargetReturnButton';

class ViewStore {
    stocks: StockResponse[] = [];
    indexPerformance: StockIndexResponse[] = [];
    briefDate: string | null = null;

    targetReturn: 'daily' | 'mtd' | 'ytd' = 'daily';

    graphItems: ChangeBarGraphItem<{ ticker: string }>[] = [];

    isLoading: boolean = true;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        runInAction(() => {
            this.isLoading = true;
        });

        try {
            const [stocks, indexPerformance] = await Promise.all([
                apiClient.stockIndex.public.getBigTechStocks.$query(),
                apiClient.stockIndex.public.getIndexPerformance.$query(),
            ]);

            runInAction(() => {
                this.stocks = stocks as StockResponse[];
                this.indexPerformance = indexPerformance;
                this.briefDate = _.last(indexPerformance)?.price?.date || null;

                this.makeGraphItems();
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    private makeGraphItems() {
        // S&P 500 변화율 추출
        const sp500Index = this.indexPerformance.find((index) => index.ticker === '_SP500');
        let sp500Return = 0;
        if (this.targetReturn === 'daily') {
            sp500Return = Number(sp500Index?.price?.dailyReturn) || 0;
        } else if (this.targetReturn === 'mtd') {
            sp500Return = Number(sp500Index?.price?.mtdReturn) || 0;
        } else if (this.targetReturn === 'ytd') {
            sp500Return = Number(sp500Index?.price?.ytdReturn) || 0;
        }

        // 빅테크 종목 아이템 생성
        const stockItems = this.stocks.map((stock) => {
            let targetReturn = 0;
            if (this.targetReturn === 'daily') {
                targetReturn = Number(stock.price?.dailyReturn) || 0;
            } else if (this.targetReturn === 'mtd') {
                targetReturn = Number(stock.price?.mtdReturn) || 0;
            } else if (this.targetReturn === 'ytd') {
                targetReturn = Number(stock.price?.ytdReturn) || 0;
            }

            return {
                name: stock.ticker,
                changePercent: targetReturn,
                data: { ticker: stock.ticker },
            };
        });

        // S&P500 + 빅테크 종목, 수익률 순 정렬 (ChangeBarGraph가 자동 정렬)
        this.graphItems = _.uniqBy(
            _.compact([
                ...stockItems,
                {
                    name: 'S&P500',
                    changePercent: sp500Return,
                    isBenchmark: true,
                },
            ]),
            'name',
        );
    }

    changeTargetReturn(target: 'daily' | 'mtd' | 'ytd') {
        this.targetReturn = target;
        this.fetch();
    }

    get highlightPercent() {
        return this.targetReturn === 'daily' ? 5 : this.targetReturn === 'mtd' ? 15 : 30;
    }
}

const BigTechBriefCard = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    return (
        <div>
            <ChangeBarGraph<{ ticker: string }>
                items={viewStore.graphItems}
                highligthPercent={viewStore.highlightPercent}
                className="-mx-5 mt-4 grow justify-between gap-2"
                keepItemOrder={false}
                isShowArrow={(item) => {
                    return !item.isBenchmark;
                }}
                onClickBar={(item) => {
                    if (item.isBenchmark) {
                        return;
                    }

                    const ticker = item.data?.ticker;
                    if (ticker) {
                        modalStore.haewao.stock({
                            ticker,
                        });
                    }
                }}
            />
            {viewStore.briefDate && (
                <TargetReturnButton
                    className="mt-8"
                    targetDate={viewStore.briefDate}
                    targetReturn={viewStore.targetReturn}
                    onChangeTargetReturn={(target) => viewStore.changeTargetReturn(target)}
                />
            )}
        </div>
    );
});

export default BigTechBriefCard;
